import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Calendar, MapPin, Tag, X, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../mock';

const Registration = () => {
  const { registration } = mockData;
  const navigate = useNavigate();
  const location = useLocation();
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Configurações do WhatsApp
  const seuNumero = "5511999999999"; // SUBSTITUA PELO SEU NÚMERO COM DDD
  const mensagem = encodeURIComponent("Quero efetuar o pagamento do Retiro Unificados 2026");
  const linkWhatsapp = `https://wa.me/${seuNumero}?text=${mensagem}`;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('inscricao') === 'sucesso') {
      setShowPaymentModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  const handleManifestarInteresse = () => {
    navigate('/inscricao');
    window.scrollTo(0, 0);
  };

  return (
    <section id="inscricao" className="py-20 bg-gradient-to-b from-white to-orange-50/30 relative">
      
      {/* --- MODAL DE PAGAMENTO --- */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-[#071227]/90 flex items-center justify-center z-[100] p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center border-t-8 border-orange-500 shadow-2xl relative animate-in fade-in zoom-in duration-300">
            <button 
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
            
            <div className="mb-6 flex justify-center">
              <div className="bg-orange-100 p-4 rounded-full">
                <Check className="text-orange-600" size={32} />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">Inscrição Enviada! 🎉</h2>
            <p className="text-gray-600 mb-6">
              Sua ficha foi recebida com sucesso. Escolha como deseja prosseguir com o pagamento:
            </p>

            {/* Opção PIX */}
            <div className="bg-gray-50 p-5 rounded-2xl border border-dashed border-gray-300 mb-4">
              <p className="text-xs text-orange-600 font-bold uppercase tracking-widest mb-1">Chave PIX</p>
              <p className="text-lg font-mono font-bold text-gray-800 break-all">financeiro.unificados@gmail.com</p>
            </div>

            <div className="flex flex-col gap-3">
              {/* BOTÃO WHATSAPP COM A MENSAGEM ESPECÍFICA */}
              <a 
                href={linkWhatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#20ba5a] transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} />
                Pagar via WhatsApp
              </a>

              <Button 
                onClick={() => setShowPaymentModal(false)}
                variant="outline"
                className="w-full py-4 border-gray-200 text-gray-500 font-semibold rounded-xl hover:bg-gray-50"
              >
                Fechar e pagar depois
              </Button>
            </div>
            
            <p className="text-[10px] text-gray-400 mt-4 uppercase tracking-tighter">
              Lembre-se: A vaga só é garantida após o comprovante.
            </p>
          </div>
        </div>
      )}

      {/* --- CONTEÚDO ORIGINAL DO REGISTRATION --- */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {registration.title}
          </h2>
          <p className="text-2xl text-orange-600 font-semibold">
            {registration.subtitle}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-br from-[#071227] to-[#0f1724] p-8 md:p-12 text-center relative overflow-visible rounded-t-xl">
            <div className="absolute -left-20 top-8 w-72 h-72 rounded-full bg-gradient-to-br from-[#ff6a3d]/12 to-[#7b2ff7]/8 blur-3xl pointer-events-none" />
            <div className="absolute right-6 -top-12 w-56 h-56 rounded-full bg-gradient-to-br from-[#ff3b6b]/10 to-[#ffd6c2]/6 blur-3xl pointer-events-none" />
            
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-extrabold text-white mb-6 tracking-tight">Detalhes do Retiro</h3>
              <div className="grid md:grid-cols-3 gap-6 text-white items-center">
                <div className="flex flex-col items-center md:items-start">
                  <div className="inline-flex items-center gap-2 text-orange-300 mb-2">
                    <Calendar size={18} />
                    <span className="text-sm text-orange-200">Data</span>
                  </div>
                  <p className="text-lg md:text-xl font-semibold">{registration.details.date}</p>
                </div>

                <div className="flex flex-col items-center md:items-center">
                  <div className="inline-flex items-center gap-2 text-orange-300 mb-2">
                    <MapPin size={18} />
                    <span className="text-sm text-orange-200">Local</span>
                  </div>
                  <p className="text-lg md:text-xl font-semibold">{registration.details.location}</p>
                </div>

                <div className="flex flex-col items-center md:items-end">
                  <div className="inline-flex items-center gap-2 text-orange-300 mb-2">
                    <Tag size={18} />
                    <span className="text-sm text-orange-200">Investimento</span>
                  </div>
                  <p className="text-2xl md:text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-rose-500">
                    {registration.details.price}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-12">
            <h4 className="text-xl font-bold text-gray-900 mb-6">O que está incluído:</h4>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {registration.details.includes.map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-orange-400 to-rose-500 rounded-full flex items-center justify-center">
                    <Check size={16} className="text-white" />
                  </div>
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Button
                onClick={handleManifestarInteresse}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white font-bold text-lg px-12 py-6 shadow-2xl transition-all transform hover:-translate-y-0.5 active:translate-y-0.5 w-full sm:w-auto rounded-xl"
              >
                {registration.cta}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Registration;